import {
    AfterViewInit,
    Component,
    Inject,
    Input,
    OnChanges,
    OnInit,
    SimpleChanges,
} from '@angular/core';
import { SplcCoordinate } from '@bds/models';
import { RtvGpsLocation } from '@bds/railtrac-models';
import { SplcCoordinateService } from '@bds/services';
import { faRss } from '@fortawesome/pro-regular-svg-icons';
import * as Leaflet from 'leaflet';
import 'leaflet-routing-machine';

export interface LeafletTileLayerOptions {
    attribution: string;
    mapUrl: string;
}

export abstract class LeafletSateliteLayerOptions implements LeafletTileLayerOptions {
    attribution: string;
    mapUrl: string;
}

export abstract class LeafletStreetLayerOptions implements LeafletTileLayerOptions {
    attribution: string;
    mapUrl: string;
}

@Component({
    selector: 'bds-gps-location-map',
    templateUrl: './bds-gps-location-map.component.html',
    styleUrls: ['./bds-gps-location-map.component.scss'],
})
export class BdsGpsLocationMapComponent implements OnInit, OnChanges, AfterViewInit {
    @Input() coordinateList: RtvGpsLocation[] = [];

    private _latestUiCallId: number = 0;

    cities: Leaflet.LayerGroup;
    iconRoute = faRss;
    latlngs: Leaflet.LatLngTuple[] = [];
    leaflet: Leaflet.Map;
    mapElementId: string = 'leafletMap';
    origin: Leaflet.Marker;
    originIcon: Leaflet.Icon = Leaflet.icon({
        iconUrl: '../assets/images/marker-icon-origin.png',
        iconSize: Leaflet.point(15, 25),
        iconAnchor: Leaflet.point(12, 25),
    });
    selectedIcon: Leaflet.Icon = Leaflet.icon({
        iconUrl: '../assets/images/marker-icon-blank.png',
        iconSize: Leaflet.point(25, 41),
        iconAnchor: Leaflet.point(12, 41),
    });
    destinationIcon: Leaflet.Icon = Leaflet.icon({
        iconUrl: '../assets/images/marker-icon-destination.png',
        iconSize: Leaflet.point(15, 25),
        iconAnchor: Leaflet.point(12, 25),
    });
    selectedMarkerOptions: Leaflet.MarkerOptions = {
        icon: this.selectedIcon,
    };
    originMarkerOptions: Leaflet.MarkerOptions = {
        icon: this.originIcon,
    };
    destinationMarkerOptions: Leaflet.MarkerOptions = {
        icon: this.destinationIcon,
    };
    sateliteLayer: Leaflet.TileLayer;
    streetLayer: Leaflet.TileLayer;
    validMapInput: boolean = false;
    viewInit: boolean = false;

    constructor(
        private splcCoordinateService: SplcCoordinateService,
        @Inject(LeafletSateliteLayerOptions)
        private leafletSatLayerOpts: LeafletSateliteLayerOptions,
        @Inject(LeafletStreetLayerOptions)
        private leafletStreetLayerOpts: LeafletStreetLayerOptions,
    ) {
        var createLeafletTileLayerFromOptions = (
            opts: LeafletTileLayerOptions,
        ): Leaflet.TileLayer => {
            return Leaflet.tileLayer(opts.mapUrl, {
                attribution: opts.attribution,
            });
        };

        this.sateliteLayer = createLeafletTileLayerFromOptions(leafletSatLayerOpts);
        this.streetLayer = createLeafletTileLayerFromOptions(leafletStreetLayerOpts);
    }

    fetchLeafletInfo(id: number): void {
        //: no coordinates = no map
        if (!this.coordinateList || this.coordinateList.length == 0) {
            this.leaflet = void 0;
            this.validMapInput = false;
            return;
        }

        var postCreateLeaflet = () => {
            if (!!this.cities) {
                this.leaflet.removeLayer(this.cities);
                this.leaflet.removeLayer(this.streetLayer);
            }

            if (id != this._latestUiCallId) {
                return;
            }

            this.createMarkersFromCoordinates();
        };

        this.initLeaflet(postCreateLeaflet, id);
    }

    getLeafletBounds(): Leaflet.LatLngBounds {
        return Leaflet.latLngBounds([this.origin.getLatLng()]);
    }

    initLeaflet(callback: Function, id: number): void {
        if (!!this.leaflet) {
            callback();
            return;
        }

        if (!document.getElementById(this.mapElementId)) {
            setTimeout(() => this.initLeaflet(callback, id), 100);
            return;
        }

        if (id != this._latestUiCallId) {
            return;
        }

        this.leaflet = Leaflet.map(this.mapElementId, {
            zoomControl: false,
        });

        var baseMaps = {
            Satelite: this.sateliteLayer,
            Streets: this.streetLayer,
        };

        Leaflet.control.layers(baseMaps).addTo(this.leaflet);
        callback();
    }

    ngAfterViewInit(): void {}

    ngOnChanges(changes: SimpleChanges): void {
        if (!!changes.coordinateList) {
            this.validMapInput = true;
            this.fetchLeafletInfo(++this._latestUiCallId);
        }
    }

    ngOnInit() {}

    createMarkersFromCoordinates(): void {
        if (!this.coordinateList || this.coordinateList.length === 0) {
            this.validMapInput = false;
            this.leaflet = void 0;
            return;
        }

        const markers: Leaflet.Marker[] = [];
        const bounds = Leaflet.latLngBounds([]);

        // Create a marker for each coordinate
        if (this.coordinateList.length <= 3) {
            this.coordinateList.forEach((coord, index) => {
                // Correct order: latitude, then longitude
                const latLng = Leaflet.latLng(coord.longitude, coord.latitude);
                let marker: Leaflet.Marker;
                const options = coord.isMain
                    ? this.selectedMarkerOptions
                    : coord.toolTip?.toLowerCase() === 'destination'
                    ? this.destinationMarkerOptions
                    : this.originMarkerOptions;
                marker = Leaflet.marker(latLng, options);

                // Label the first marker as Origin, others as points
                const tooltipText = `${coord?.toolTip ?? ''}`;
                marker.bindTooltip(tooltipText);

                markers.push(marker);
                bounds.extend(latLng);
            });
        } else {
            // Remove any existing routing layers
            this.leaflet.eachLayer((layer: any) => {
                if (layer instanceof Leaflet.Polyline && !(layer instanceof Leaflet.Polygon)) {
                    this.leaflet.removeLayer(layer);
                }
            });
            // For lists longer than 3 points, draw polyline through all coordinates
            const waypoints = this.coordinateList.map((coord) =>
                Leaflet.latLng(coord.longitude, coord.latitude),
            );

            // Draw railroad polyline through all points
            Leaflet.polyline(waypoints, {
                color: '#0b1dbeff',
                weight: 4,
                opacity: 0.8,
                dashArray: '5, 5',
            }).addTo(this.leaflet);

            this.leaflet.addLayer(this.cities);
            this.leaflet.addLayer(this.streetLayer);
        }

        if (
            this.coordinateList.find((coord) => coord.toolTip?.toLowerCase() === 'origin') &&
            this.coordinateList.find((coord) => coord.toolTip?.toLowerCase() === 'destination')
        ) {
            const origin = this.coordinateList.find(
                (coord) => coord.toolTip?.toLowerCase() === 'origin' && !coord.isMain,
            );
            const destination = this.coordinateList.find(
                (coord) => coord.toolTip?.toLowerCase() === 'destination' && !coord.isMain,
            );

            if (origin && destination && markers.length > 1) {
                const originLatLng = Leaflet.latLng(origin.longitude, origin.latitude);
                const destLatLng = Leaflet.latLng(destination.longitude, destination.latitude);

                // Remove any existing routing layers
                this.leaflet.eachLayer((layer: any) => {
                    if (layer instanceof Leaflet.Polyline && !(layer instanceof Leaflet.Polygon)) {
                        this.leaflet.removeLayer(layer);
                    }
                });

                // Use Leaflet Routing Machine to follow actual railroad routes
                const routingControl = (Leaflet as any).Routing.control({
                    waypoints: [originLatLng, destLatLng],
                    router: (Leaflet as any).Routing.osrmv1({
                        serviceUrl: 'https://router.project-osrm.org/route/v1',
                    }),
                    routeWhileDragging: false,
                    createMarker: () => null,
                    lineOptions: {
                        styles: [
                            {
                                color: '#0b1dbeff',
                                weight: 4,
                                opacity: 0.8,
                                dashArray: '5, 5',
                            },
                        ],
                    },
                }).addTo(this.leaflet);

                // Hide the routing panel
                if (routingControl._container) {
                    routingControl._container.style.display = 'none';
                }
            }
        }

        // Keep reference to first marker as origin
        this.origin = markers[0];
        this.origin.openTooltip();

        this.cities = Leaflet.layerGroup(markers);
        this.leaflet.addLayer(this.cities);
        this.leaflet.addLayer(this.streetLayer);

        // Fit the map to show all markers
        if (this.coordinateList.length === 1) {
            this.leaflet.setView(this.origin.getLatLng(), 16);
        } else {
            this.leaflet.fitBounds(bounds, { padding: [50, 50] });
        }
    }
}
